import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { isMemberWorking, slotsOf } from './time';

/**
 * 2days 워크보드 규칙 — "2days project · MVP 확정본 v1.3" 기준.
 * 데이터 모양은 ./mockData.js 상단 주석 참고.
 */

export const cn = (...args) => twMerge(clsx(args));

/* ── 이름표 ───────────────────────────────────────────── */

/** 직군 (team_members.job). 권한은 직군이 아니라 role(leader·member)로 정한다 */
export const JOB_LABEL = {
  planner: 'PM',
  designer: 'Designer',
  developer: 'Developer',
  marketer: 'Marketer',
  npc_lead: 'Ops',
};

/** 팀 목록·담당자 열 순서 — 리더 먼저, 그다음 인계 순서(기획 → 디자인 → 개발 → 마케팅) */
const JOB_ORDER = ['planner', 'designer', 'developer', 'marketer', 'npc_lead'];

export const byJob = (a, b) =>
  (b.role === 'leader') - (a.role === 'leader') ||
  JOB_ORDER.indexOf(a.job) - JOB_ORDER.indexOf(b.job);

/** 리더 — 팀에 1명. 진행 중 배정·타임라인 조정·general 체크포인트를 맡는다 */
export const isLeader = (m) => m?.role === 'leader';

/** 팀 목록·사이드바의 역할 표기 — 리더는 "PM · Leader", 팀원은 직군만 */
/** 스태프 표시 — 스태프 팀원이거나 코레디 백업 인원(staffBackup)이면 Staff */
export const isStaffShown = (m) => !!m && (m.staff || m.staffBackup);

/** "Designer" · "PM · Leader" · "Marketer · Staff" — 사이드바 내 카드 · 팀 패널 · 타임라인 */
export const roleLabel = (m) =>
  m
    ? `${JOB_LABEL[m.job] ?? ''}${isLeader(m) ? ' · Leader' : ''}${isStaffShown(m) ? ' · Staff' : ''}`
    : '';
export const isNpc = (m) => m?.role === 'npc';

/**
 * 프로젝트를 마쳤는지 — 종료(ended) · 종료 시각이 지남 · 내 동료 평가 제출.
 * 마친 뒤에는 체크인 · 댓글(답글 · 고치기 · 지우기 · 반응)을 남기지 않는다 (서버도 막는다)
 */
export const isClosedFor = (data, now) =>
  data?.project?.status === 'ended' ||
  (!!data?.project?.endAt && now.getTime() >= new Date(data.project.endAt).getTime()) ||
  !!data?.completeProject?.submittedAt;
export const closedTipFor = (data) =>
  data?.completeProject?.submittedAt
    ? 'You’ve completed the project. Comments and check-ins are closed.'
    : 'The project has ended. Comments and check-ins are closed.';
export const leaderOf = (members) => [...members].find(isLeader) ?? null;

/** 첫 단어 + 마지막 단어의 첫 글자 ("Team lead Kim" → TK) */
export const initials = (name) => {
  const words = name
    .replace(/\(.*?\)/g, '')
    .trim()
    .split(/\s+/);
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return (first + last).toUpperCase();
};

/* ── 상태 → 화면 ───────────────────────────────────────── */

/** 상태를 사람이 읽는 이름으로. `rework_requested` 는 화면에서 "Needs changes" */
export const STATE_LABEL = {
  assigned: 'Assigned',
  declined: 'Declined',
  in_progress: 'In progress',
  submitted: 'In review',
  rework_requested: 'Needs changes',
  approved: 'Approved',
  cancelled: 'Cancelled',
};

/** 칩 색 — 디자인 시스템 functional 색 이름 그대로 */
export const STATE_TONE = {
  assigned: 'gray',
  declined: 'gray',
  in_progress: 'gray',
  submitted: 'accent',
  rework_requested: 'warning',
  approved: 'success',
  cancelled: 'gray',
};

/** 담당자 전원 — 공동 담당자면 여럿 (첫 사람 = 대표 assigneeId) */
export const assigneesOf = (task) =>
  task?.assigneeIds?.length ? task.assigneeIds : task?.assigneeId ? [task.assigneeId] : [];

/** 이 사람이 담당자(공동 포함)인가 */
export const isAssigneeOf = (task, id) => !!id && assigneesOf(task).includes(id);

/**
 * 내가 받은 새 배정 — 수락·거절을 기다리는 내 태스크.
 * 수락 대기 시간(MVP §8-6)이 흐르고 있으니 눈에 띄게 표시한다.
 */
export const isNewRequestFor = (task, viewerId) =>
  task.state === 'assigned' && isAssigneeOf(task, viewerId);

/** 마감(보정 기한)이 지난 뒤 흐른 분 — 아직 제출 전인 태스크만. 아니면 0 */
export const overdueMinutes = (task, ctx = {}) => {
  if (!ctx.now || !['assigned', 'in_progress', 'rework_requested'].includes(task.state)) return 0;
  const end = ctx.data?.project?.endAt;
  const due = ctx.taskById && end ? effectiveDue(task, ctx.taskById, ctx.now, end) : new Date(task.dueAt);
  const diff = ctx.now.getTime() - due.getTime();
  return diff > 0 ? Math.max(1, Math.floor(diff / 60_000)) : 0;
};

/** "35m" · "1h 20m" */
export const minutesLabel = (m) =>
  m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m}m`;

/**
 * 배정(재배정) 뒤 수락·거절 없이 흐른 시간(분) — 배정 상태일 때만.
 * 담당자를 알면 그 사람의 작업 시간 안에서 흐른 시간만 센다 (Flow Chart §6 · offline 시간 제외)
 */
export const acceptWaitMinutes = (task, now, member) => {
  if (task.state !== 'assigned' || !task.assignedAt || !now) return null;
  const from = new Date(task.assignedAt).getTime();
  const to = now.getTime();
  if (to <= from) return 0;
  if (!member?.timezone || !slotsOf(member).length) {
    return Math.floor((to - from) / 60000);
  }
  // 5분 단위로 훑는다 (프로젝트는 이틀이라 많아야 600번 남짓)
  const STEP = 5 * 60000;
  let mins = 0;
  for (let t = from; t < to; t += STEP) {
    if (isMemberWorking(new Date(t), member)) {
      mins += Math.min(STEP, to - t) / 60000;
    }
  }
  return Math.floor(mins);
};

/**
 * 뒤 태스크를 막지 않는 앞 단계 상태 — 취소 · 거절(declined).
 * 앞 단계가 거절되면 다시 배정될 때까지 뒤 태스크는 그대로 진행한다 (막지 않고 마감도 밀지 않는다)
 */
export const NON_BLOCKING = ['cancelled', 'declined'];

/** 아직 승인되지 않은 앞 단계 (취소 · 거절된 것은 막지 않는다) */
export const waitingOnOf = (task, taskById) =>
  (task.predecessorIds ?? [])
    .map((id) => taskById?.get(id))
    .filter((p) => p && p.state !== 'approved' && !NON_BLOCKING.includes(p.state));

/** 상태 이름 — 최종 결과물이 검토 중이면 Final review */
export const stateLabelOf = (task) =>
  task.isFinal && task.state === 'submitted' ? 'Final review' : STATE_LABEL[task.state];

/**
 * 카드·상세 칩 (Task Flows tb): 상태 · Blocked · 수락 대기(30분~ Accept pending, 60분~ No response) · Waiting on N tasks
 * @param ctx { now, taskById } — 없으면 시간·앞 단계 칩은 빼고 그린다
 * @param labels { [state]: 이름 } — 상태 이름 바꿔 부르기 (선택)
 */
export const chipsFor = (task, viewerId, ctx = {}, labels = null) => {
  const chips = [];
  if (isNewRequestFor(task, viewerId)) {
    chips.push({ label: 'New request', tone: 'newRequest' });
  }
  // labels — 화면마다 상태 이름을 바꿔 부를 때 (My tasks 의 rework_requested = Changes requested)
  chips.push({ label: labels?.[task.state] ?? stateLabelOf(task), tone: STATE_TONE[task.state] });
  // 막힘은 상태가 아닌 표시 — 상태 칩 옆에 붙는다 (MVP #6·§7-5)
  if (task.isBlocked) chips.push({ label: 'Blocked', tone: 'failed' });
  // 마감이 지났는데 아직 제출 전 — 보드 · 목록 · 상세 어디서나 보이게
  const late = overdueMinutes(task, ctx);
  if (late > 0) chips.push({ label: `Overdue · ${minutesLabel(late)}`, tone: 'failed' });
  if (task.listChanged) chips.push({ label: 'List changed', tone: 'warning' });
  // 공동 태스크를 다른 담당자가 수락했으면 누가 수락했는지 — 내가 수락하지 않았는데 진행 중인 이유 (2DP-T17-1)
  if (
    task.acceptedById &&
    task.acceptedById !== viewerId &&
    isAssigneeOf(task, viewerId) &&
    ['in_progress', 'rework_requested'].includes(task.state)
  ) {
    const by = ctx.memberById?.get(task.acceptedById)?.name;
    chips.push({ label: by ? `Accepted by ${by}` : 'Accepted by a teammate', tone: 'gray' });
  }
  // 공동 담당자면 보는 사람(담당자일 때) 기준, 아니면 대표 기준
  const waiter = isAssigneeOf(task, viewerId) ? viewerId : task.assigneeId;
  const wait = acceptWaitMinutes(task, ctx.now, ctx.memberById?.get(waiter));
  if (wait >= 60) chips.push({ label: `No response · ${minutesLabel(wait)}`, tone: 'failed' });
  else if (wait >= 30)
    chips.push({ label: `Accept pending · ${minutesLabel(wait)}`, tone: 'warning' });
  const gate = waitingOnOf(task, ctx.taskById);
  if (gate.length && ['assigned', 'in_progress', 'rework_requested'].includes(task.state)) {
    chips.push({
      label: `Waiting on ${gate.length} ${gate.length === 1 ? 'task' : 'tasks'}`,
      tone: 'gray',
    });
  }
  return chips;
};

/* ── 보드 열 ───────────────────────────────────────────── */

export const BOARD_COLUMNS = [
  { key: 'todo', label: 'To do', states: ['assigned', 'declined'] },
  {
    key: 'in_progress',
    label: 'In progress',
    states: ['in_progress', 'rework_requested'],
  },
  { key: 'in_review', label: 'In review', states: ['submitted'] },
  { key: 'done', label: 'Done', states: ['approved'] },
];

/** 열이 없는 보기(보드·담당자별·타임라인)에 올릴 태스크 — 취소 제외 */
export const visibleTasks = (tasks) => tasks.filter((t) => t.state !== 'cancelled');

/* ── 기한 ──────────────────────────────────────────────── */

const ms = (iso) => new Date(iso).getTime();

/**
 * 보정 기한 (MVP §8-4). 저장하지 않고 인계 체인에서 계산한다.
 *
 *   보정 기한 = due_at + 앞 단계 지연(앞 태스크가 여럿이면 최댓값) + 되돌림 대기
 *   앞 단계 지연 = max(0, 앞 태스크의 승인된 버전 제출 시각 − 앞 태스크 due_at)
 *
 * 앞 태스크가 아직 승인된 버전을 내지 않았으면 지금 시각까지 늦어진 만큼으로 본다
 * — 화면에서 뒷사람이 지금 얼마나 밀렸는지 바로 보이게 하기 위해서다.
 * 프로젝트 종료 시각은 넘지 않는다.
 *
 * @returns {Date|null} 보정이 없으면 null
 */
export const adjustedDueAt = (task, byId, now, projectEnd) => {
  let upstreamDelay = 0;
  task.predecessorIds.forEach((id) => {
    const prev = byId.get(id);
    if (!prev || NON_BLOCKING.includes(prev.state)) return;
    const doneAt = prev.approvedSubmittedAt ? ms(prev.approvedSubmittedAt) : now.getTime();
    upstreamDelay = Math.max(upstreamDelay, doneAt - ms(prev.dueAt));
  });
  const total = Math.max(0, upstreamDelay) + (task.returnWaitMinutes ?? 0) * 60000;
  if (total <= 0) return null;
  return new Date(Math.min(ms(task.dueAt) + total, ms(projectEnd)));
};

/** 화면 정렬·타임라인에 쓰는 실제 마감 — 보정이 있으면 보정 기한 */
export const effectiveDue = (task, byId, now, projectEnd) =>
  adjustedDueAt(task, byId, now, projectEnd) ?? new Date(task.dueAt);

/* ── 사이드바 배지 ─────────────────────────────────────── */

/** My tasks 배지 — 내 수락을 기다리는 새 배정 수 */
export const newRequestCount = (data) =>
  // 열어 본 요청은 세지 않는다 — New request 표시는 수락·거절 전까지 남는다 (Task Flows 9)
  data.tasks.filter((t) => isNewRequestFor(t, data.viewerId) && !t.openedAt).length;

/** Checkpoints 배지 — 지금 체크인할 때인(Due now) 체크포인트 수 */
export const checkpointDueCount = (data, now, seen = null) => {
  const viewer = data.members.find((m) => m.id === data.viewerId);
  return (data.checkpoints ?? []).filter(
    (c) => isDueNow(c, viewer, data.checkins ?? [], now) && !seen?.has(c.id),
  ).length;
};

/*
 * Due now · Seen — Checkpoints 페이지를 열면 그때 Due now 인 체크포인트는 본 것으로 치고 사이드바 배지를 지운다.
 * 이 기기에만 기억한다 (사용자 · 프로젝트별). 바뀌면 'td-cp-seen' 이벤트로 레이아웃에 알린다
 */
const cpSeenKey = (data) => `2days-cp-seen:${data.project.id}:${data.viewerId}`;
export const CP_SEEN_EVENT = 'td-cp-seen';
export const loadSeenCheckpoints = (data) => {
  try {
    return new Set(JSON.parse(window.localStorage.getItem(cpSeenKey(data)) ?? '[]'));
  } catch {
    return new Set();
  }
};
export const markCheckpointsSeen = (data, ids) => {
  const seen = loadSeenCheckpoints(data);
  const before = seen.size;
  ids.forEach((id) => seen.add(id));
  if (seen.size === before) return;
  try {
    window.localStorage.setItem(cpSeenKey(data), JSON.stringify([...seen]));
  } catch {
    // 저장이 막혀도 이번 화면에서는 지운 것으로 둔다
  }
  window.dispatchEvent(new CustomEvent(CP_SEEN_EVENT, { detail: [...seen] }));
};

/* ── 팀원 ──────────────────────────────────────────────── */

/**
 * 실제 접속 — 워크보드를 열어 둔 사람 (보드를 15초마다 다시 읽으니 최근 2분 안에 읽었으면 Online). 나는 늘 접속 중.
 * 작업 시간 밖(Off hours, presenceOf 의 offline)과 다르다 — 계산(수락 대기 · 체크인 마감 등)은 작업 시간 기준이다
 */
export const ONLINE_MS = 2 * 60 * 1000;
export const isOnline = (m, now, viewerId) =>
  !!m &&
  (m.id === viewerId ||
    (!!m.lastSeenAt && now.getTime() - new Date(m.lastSeenAt).getTime() <= ONLINE_MS));

/** 접속하지 않은 사람 — "Offline · last seen 5m ago", 한 번도 안 열었으면 그냥 "Offline" */
export const lastSeenLabel = (m, now) => {
  if (!m?.lastSeenAt) return 'Offline';
  const mins = Math.max(1, Math.floor((now.getTime() - new Date(m.lastSeenAt).getTime()) / 60000));
  if (mins < 60) return `Offline · last seen ${mins}m ago`;
  if (mins < 24 * 60) return `Offline · last seen ${Math.floor(mins / 60)}h ago`;
  return 'Offline · last seen over a day ago';
};

/** 접속 표시 — "Online" 또는 "Offline (· last seen …)" */
export const connectionLabel = (m, now, viewerId) =>
  isOnline(m, now, viewerId) ? 'Online' : lastSeenLabel(m, now);

/** offline = 지금이 본인 작업 시간이 아님(화면에는 Off hours). 이탈과 다르다 (MVP §10-2) */
export const presenceOf = (m, now) => {
  if (m.status === 'dropped') return 'dropped';
  return isMemberWorking(now, m) ? 'active' : 'offline';
};

/* ── 체크포인트 (requested · general) ─────────────────────── */

/** 이 체크포인트를 받는 사람인가 — requested 는 팀 전체, general 은 대상 직군만 */
export const isTargetOf = (cp, member) =>
  !!member &&
  member.role !== 'npc' &&
  !member.staff && // 스태프는 체크포인트 대상이 아니다 (평가 제외)
  member.status === 'active' &&
  (cp.type === 'requested' || (cp.targetJobs ?? []).includes(member.job));

/**
 * 체크인 마감 시각. 보통은 체크포인트 시각 그대로(허용폭 0분).
 * 체크포인트가 그 사람의 작업 시간 밖이면, 다시 작업 시간이 시작된 뒤 30분까지 (MVP §8-10).
 */
export const checkinDeadline = (cp, member) => {
  const at = new Date(cp.scheduledAt).getTime();
  if (!member || isMemberWorking(new Date(at), member)) {
    return at;
  }
  const STEP = 15 * 60 * 1000;
  for (let t = at + STEP; t <= at + 24 * 3_600_000; t += STEP) {
    if (isMemberWorking(new Date(t), member)) {
      return t + 30 * 60 * 1000;
    }
  }
  return at;
};

/**
 * 체크인 상태.
 *   on_time  마감(위)까지 체크인
 *   late     마감이 지난 뒤 체크인 (지각 제출)
 *   missed   마감이 지났고 체크인 없음 — 지금도 지각 제출은 할 수 있다
 *   upcoming 아직 마감 전 — 체크인은 체크포인트 30분 전부터 열린다 (checkinOpensAt)
 */
export const checkinStatus = (cp, member, checkins, now) => {
  const ci = checkins.find((c) => c.checkpointId === cp.id && c.userId === member?.id);
  const deadline = checkinDeadline(cp, member);
  if (ci) return new Date(ci.checkedInAt).getTime() <= deadline ? 'on_time' : 'late';
  return deadline > now.getTime() ? 'upcoming' : 'missed';
};

/** Due now — 체크포인트 30분 전부터 체크인 마감(정시, 오프라인이면 복귀 후 30분)까지, 아직 체크인 전 */
export const DUE_LEAD_MS = 30 * 60 * 1000;

/**
 * 체크인이 열리는 시각 — 체크포인트 30분 전 (서버와 같다). 그 전에는 미리 체크인할 수 없다.
 * 열린 뒤에는 마감이 지나도 지각 체크인 · 수정은 계속 된다
 */
/** 프로젝트 마감 — 마지막 태스크 마감(시작 + 47시간)과 종료 중 이른 쪽. 예상 제출 · 체크포인트는 이 뒤로 고를 수 없다 */
export const projectDeadline = (project) =>
  new Date(
    Math.min(
      new Date(project.endAt).getTime(),
      new Date(project.startAt).getTime() + 47 * 3_600_000,
    ),
  );

export const checkinOpensAt = (cp) => new Date(cp.scheduledAt).getTime() - DUE_LEAD_MS;
export const isCheckinOpen = (cp, now) => now.getTime() >= checkinOpensAt(cp);
export const isDueNow = (cp, member, checkins, now) => {
  if (!isTargetOf(cp, member)) return false;
  if (checkins.some((c) => c.checkpointId === cp.id && c.userId === member.id)) return false;
  const t = now.getTime();
  return t >= new Date(cp.scheduledAt).getTime() - DUE_LEAD_MS && t <= checkinDeadline(cp, member);
};

/** 오프라인 중 생긴 체크포인트에 복귀 후 30분 안에 체크인 — On time 옆에 "Back from offline" */
export const isBackFromOffline = (cp, ci) =>
  !!ci && new Date(ci.checkedInAt) > new Date(cp.scheduledAt);

/**
 * 팀 탭 한 줄 요약 — 놓친 사람 > 늦은 사람 > 모두 제때 > 아직 열려 있음 순으로 하나만.
 * @returns {{ targets, checkedIn, chip }}
 */
export const checkpointSummary = (cp, members, checkins, now) => {
  const targets = members.filter((m) => isTargetOf(cp, m)).sort(byJob);
  const statuses = targets.map((m) => checkinStatus(cp, m, checkins, now));
  const count = (s) => statuses.filter((x) => x === s).length;
  const checkedIn = targets.filter((m) =>
    checkins.some((c) => c.checkpointId === cp.id && c.userId === m.id),
  );
  let chip;
  if (count('missed')) chip = { label: `${count('missed')} missed`, tone: 'failed' };
  else if (count('late')) chip = { label: `${count('late')} late`, tone: 'warning' };
  // 시각 전이면 일부가 미리 체크인했어도 Upcoming (Task Flows W6 Team)
  else if (count('upcoming') === targets.length || now < new Date(cp.scheduledAt))
    chip = CHECKIN_STATUS.upcoming;
  else if (count('upcoming')) chip = { label: 'Open', tone: 'accent' };
  else chip = { label: 'All on time', tone: 'success' };
  return { targets, checkedIn, chip };
};

export const CHECKIN_STATUS = {
  on_time: { label: 'On time', tone: 'success' },
  late: { label: 'Late', tone: 'warning' },
  missed: { label: 'Missed', tone: 'failed' },
  upcoming: { label: 'Upcoming', tone: 'gray' },
};

/* ── 인계 반려 사유 (5종 + 개발 인계 3종) ─────────────────────── */

export const REJECT_REASONS = [
  { key: 'link_access', label: 'Can’t open the link or no access' },
  { key: 'final_unclear', label: 'Not clear which version is final' },
  { key: 'missing_items', label: 'Missing spec values (spacing, color, API fields)' },
  { key: 'undecided', label: 'Open decisions remain' },
  { key: 'feedback_unclear', label: 'Not clear if earlier feedback was applied' },
  { key: 'other', label: 'Other' },
];

/** 개발 → PM(QA) 인계에서만 더 고르는 사유 — 서버 TdNotifier 와 같은 문구 */
export const DEV_REJECT_REASONS = [
  { key: 'deploy_broken', label: 'Web link doesn’t load or the core flow breaks' },
  { key: 'repo_issue', label: 'Repository is private or empty' },
  { key: 'readme_missing', label: 'README is missing required sections' },
];

/** 사유 key → 문구 (개발 사유 포함) */
export const rejectReasonLabel = (key) =>
  [...REJECT_REASONS, ...DEV_REJECT_REASONS].find((r) => r.key === key)?.label;
